import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { llmApi, projectsApi } from "@/services/rag-service";
import { organizationApi } from "@/services/organization-service";
import { useAuth } from "@/contexts/AuthContext";
import ProjectResolutionList from "./components/ProjectResolutionList";
import ShareEmailInput from "./components/ShareEmailInput";
import SharedUsersList from "./components/SharedUsersList";
import type {
  AccessLevel,
  CatalogSelectOption,
  ExistingAccessLevel,
  LLMModel,
  Project,
  ProjectMembership,
  ShareUser,
} from "./types";
import { getUserId, getUserEmail, normalizeEmail } from "./utils/catalog";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type AICatalogShareProps = {
  open: boolean;
  onClose: () => void;
  model: LLMModel | null;
  onShared: () => void | Promise<void>;
  returnFocusRef?: React.RefObject<HTMLButtonElement | null>;
  fallbackFocusRef?: React.RefObject<HTMLElement | null>;
};

type ProjectResolutionStatus = "loading" | "resolved" | "error";

type SharePayload = {
  invalidShareEmails: string[];
  organizationBlockedShareEmails: string[];
  blockedShareEmails: string[];
  readyShareEmails: string[];
};

type OrganizationMember = {
  email?: string;
  user_email?: string;
  user?: {
    email?: string;
    username?: string;
  };
};

const AICatalogShareComp = ({
  open,
  onClose,
  model,
  onShared,
  returnFocusRef,
  fallbackFocusRef,
}: AICatalogShareProps) => {
  const { t } = useTranslation();
  const { cache } = useAuth();
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [accessLevel, setAccessLevel] = useState<AccessLevel>("modifier");
  const [membershipByEmail, setMembershipByEmail] = useState<
    Record<string, ProjectMembership>
  >({});
  const [resolvedProjectEmails, setResolvedProjectEmails] = useState<
    Record<string, boolean>
  >({});
  const [linkedProject, setLinkedProject] = useState<Project | null>(null);
  const [organizationMembers, setOrganizationMembers] = useState<
    OrganizationMember[]
  >([]);
  const [sharedUsers, setSharedUsers] = useState<ShareUser[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [sharesLoadError, setSharesLoadError] = useState(false);
  const [projectResolutionStatus, setProjectResolutionStatus] =
    useState<ProjectResolutionStatus>(model?.project_id ? "loading" : "resolved");
  const [projectLoadAttempt, setProjectLoadAttempt] = useState(0);
  const [loadingOrganizationMembers, setLoadingOrganizationMembers] =
    useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const addingToProjectRef = useRef(new Set<string>());
  const updatingShareRef = useRef(new Set<string>());
  const [addingToProject, setAddingToProject] = useState<
    Record<string, boolean>
  >({});
  const [updatingShare, setUpdatingShare] = useState<Record<string, boolean>>(
    {},
  );
  const sessionRef = useRef(0);
  const openRef = useRef(open);
  const modelIdRef = useRef(model?.id);

  useLayoutEffect(() => {
    openRef.current = open;
    modelIdRef.current = model?.id;
    sessionRef.current += 1;
  }, [open, model?.id]);

  const isCurrentSession = useCallback((session: number, modelId?: string) =>
    openRef.current &&
    sessionRef.current === session &&
    modelIdRef.current === modelId, []);

  const accessLevels: CatalogSelectOption<AccessLevel>[] = useMemo(
    () => [
      { value: "modifier", label: t("aiCatalog.shareDialog.canEdit") },
      { value: "viewer", label: t("aiCatalog.shareDialog.canView") },
      { value: "admin", label: t("aiCatalog.shareDialog.canManage") },
    ],
    [t],
  );

  const projectId = model?.project_id;
  const activeLinkedProject = linkedProject?.id === projectId ? linkedProject : null;
  const organizationId =
    activeLinkedProject?.organization_id ||
    activeLinkedProject?.organization?.id ||
    null;
  const isOrganizationProject = Boolean(organizationId);
  const projectContextReady =
    !projectId ||
    (projectResolutionStatus === "resolved" &&
      activeLinkedProject?.id === projectId);
  const projectName =
    activeLinkedProject?.name ||
    model?.project_name ||
    model?.project?.name ||
    t("aiCatalog.shareDialog.fallbackProjectName");
  const modelName = model?.name || t("aiCatalog.shareDialog.fallbackModelName");
  const hasPendingMutations =
    submitting ||
    Object.values(addingToProject).some(Boolean) ||
    Object.values(updatingShare).some(Boolean);

  const organizationMemberEmails = useMemo(() => {
    const memberEmails = organizationMembers
      .map((member) =>
        normalizeEmail(
          member.user?.email ||
            member.user?.username ||
            member.email ||
            member.user_email ||
            "",
        ),
      )
      .filter(Boolean);

    return new Set(memberEmails);
  }, [organizationMembers]);

  const invalidEmails = useMemo(
    () => emails.filter((email) => !emailRegex.test(email)),
    [emails],
  );

  const organizationBlockedEmails = useMemo(
    () =>
      emails.filter(
        (email) =>
          isOrganizationProject &&
          emailRegex.test(email) &&
          !organizationMemberEmails.has(email),
      ),
    [emails, isOrganizationProject, organizationMemberEmails],
  );

  const blockedEmails = useMemo(
    () =>
      emails.filter(
        (email) =>
          emailRegex.test(email) &&
          !organizationBlockedEmails.includes(email) &&
          membershipByEmail[email]?.is_member === false,
      ),
    [emails, membershipByEmail, organizationBlockedEmails],
  );

  const projectResolutionEmails = useMemo(
    () =>
      emails.filter(
        (email) =>
          blockedEmails.includes(email) || resolvedProjectEmails[email],
      ),
    [emails, blockedEmails, resolvedProjectEmails],
  );

  const canShare = useMemo(() => {
    const typedEmails = emailInput
      .split(/[,\s]+/)
      .map(normalizeEmail)
      .filter(Boolean);
    const shareEmails = [...emails];

    typedEmails.forEach((email) => {
      if (!shareEmails.includes(email)) shareEmails.push(email);
    });

    return (
      !submitting &&
      projectContextReady &&
      !loadingOrganizationMembers &&
      shareEmails.length > 0 &&
      shareEmails.every((email) => emailRegex.test(email)) &&
      shareEmails.every(
        (email) =>
          !isOrganizationProject || organizationMemberEmails.has(email),
      ) &&
      shareEmails.every(
        (email) => membershipByEmail[email]?.is_member !== false,
      ) &&
      shareEmails.every(
        (email) =>
          !sharedUsers.find(
            (user) =>
              normalizeEmail(getUserEmail(user)) === email ||
              normalizeEmail(user.user_name || "") === email,
          ),
      )
    );
  }, [
    emailInput,
    emails,
    isOrganizationProject,
    loadingOrganizationMembers,
    projectContextReady,
    membershipByEmail,
    organizationMemberEmails,
    sharedUsers,
    submitting,
  ]);

  const resetState = () => {
    setEmails([]);
    setEmailInput("");
    setAccessLevel("modifier");
    setMembershipByEmail({});
    setResolvedProjectEmails({});
    setLinkedProject(null);
    setOrganizationMembers([]);
    setSharedUsers([]);
    setLoadingShares(false);
    setSharesLoadError(false);
    setProjectResolutionStatus(model?.project_id ? "loading" : "resolved");
    setLoadingOrganizationMembers(false);
    setSubmitting(false);
    submittingRef.current = false;
    addingToProjectRef.current.clear();
    updatingShareRef.current.clear();
    setAddingToProject({});
    setUpdatingShare({});
  };

  const checkMembership = async (email: string) => {
    const session = sessionRef.current;
    const modelId = model?.id;
    const currentProjectId = projectId;
    if (!currentProjectId || !modelId || !emailRegex.test(email)) return;

    try {
      const status = (await projectsApi.checkUserProjectMembership(
        currentProjectId,
        email,
      )) as ProjectMembership;
      if (!isCurrentSession(session, modelId)) return;
      setMembershipByEmail((previous) => ({
        ...previous,
        [email]: status || { is_member: false },
      }));
    } catch {
      if (!isCurrentSession(session, modelId)) return;
      setMembershipByEmail((previous) => ({
        ...previous,
        [email]: { is_member: false },
      }));
    }
  };

  const addEmails = (rawValue: string) => {
    const nextEmails = rawValue
      .split(/[,\s]+/)
      .map(normalizeEmail)
      .filter(Boolean);

    const userFound = nextEmails.some((email) =>
      sharedUsers.find(
        (user) =>
          normalizeEmail(getUserEmail(user)) === email ||
          normalizeEmail(user.user_name || "") === email,
      ),
    );

    if (!nextEmails.length) return;
    if (!userFound) {
      setEmails((previous) => {
        const merged = [...previous];
        nextEmails.forEach((email) => {
          if (!merged.includes(email)) merged.push(email);
        });
        return merged;
      });

      nextEmails.forEach(checkMembership);
    }
    setEmailInput("");
  };

  const removeEmail = (email: string) => {
    setEmails((previous) => previous.filter((item) => item !== email));
    setResolvedProjectEmails((previous) => {
      const next = { ...previous };
      delete next[email];
      return next;
    });
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (["Enter", "Tab", ","].includes(event.key)) {
      event.preventDefault();
      addEmails(emailInput);
    }

    if (event.key === "Backspace" && !emailInput && emails.length) {
      setEmails((previous) => previous.slice(0, -1));
    }
  };

  const loadSharedUsers = useCallback(async (
    session = sessionRef.current,
    modelId = model?.id,
  ) => {
    if (!modelId) {
      setSharedUsers([]);
      return;
    }

    setLoadingShares(true);
    setSharesLoadError(false);
    try {
      const users = (await llmApi.getLLMConfigShares(modelId)) as ShareUser[];
      if (!isCurrentSession(session, modelId)) return;
      setSharedUsers(Array.isArray(users) ? users : []);
    } catch (error) {
      if (!isCurrentSession(session, modelId)) return;
      setSharesLoadError(true);
      console.error("Error loading model shares:", error);
      toast.error(t("aiCatalog.shareDialog.failedToLoadSharedUsers"));
    } finally {
      if (isCurrentSession(session, modelId)) setLoadingShares(false);
    }
  }, [isCurrentSession, model?.id, t]);

  const handleClose = () => {
    if (
      submittingRef.current ||
      addingToProjectRef.current.size > 0 ||
      updatingShareRef.current.size > 0
    ) return;
    resetState();
    onClose();
  };

  const handleAddToProject = async (email: string) => {
    const session = sessionRef.current;
    const modelId = model?.id;
    const currentProjectId = projectId;
    if (
      !currentProjectId ||
      !modelId ||
      !projectContextReady ||
      addingToProjectRef.current.has(email)
    ) return;

    addingToProjectRef.current.add(email);
    setAddingToProject((previous) => ({ ...previous, [email]: true }));
    try {
      await projectsApi.shareProject(currentProjectId, {
        email,
        access_level: accessLevel,
      });
      if (!isCurrentSession(session, modelId)) return;
      setMembershipByEmail((previous) => ({
        ...previous,
        [email]: { is_member: true, access_level: accessLevel },
      }));
      setResolvedProjectEmails((previous) => ({ ...previous, [email]: true }));
      toast.success(
        t("aiCatalog.shareDialog.addedToProjectSuccess", { email }),
      );
    } catch (error) {
      if (!isCurrentSession(session, modelId)) return;
      console.error("Error adding user to project:", error);
      toast.error(
        (error as Error).message ||
          t("aiCatalog.shareDialog.failedToAddToProject"),
      );
    } finally {
      addingToProjectRef.current.delete(email);
      if (isCurrentSession(session, modelId)) {
        setAddingToProject((previous) => ({ ...previous, [email]: false }));
      }
    }
  };

  const getSharePayload = async (
    session: number,
    modelId: string,
  ): Promise<SharePayload | null> => {
    if (projectId && !projectContextReady) return null;
    const typedEmails = emailInput
      .split(/[,\s]+/)
      .map(normalizeEmail)
      .filter(Boolean);
    const shareEmails = [...emails];

    typedEmails.forEach((email) => {
      if (!shareEmails.includes(email)) shareEmails.push(email);
    });

    const invalidShareEmails = shareEmails.filter(
      (email) => !emailRegex.test(email),
    );
    const organizationBlockedShareEmails = shareEmails.filter(
      (email) =>
        isOrganizationProject &&
        emailRegex.test(email) &&
        !organizationMemberEmails.has(email),
    );
    let nextMembershipByEmail = membershipByEmail;

    if (projectId) {
      const emailsToCheck = shareEmails.filter(
        (email) =>
          emailRegex.test(email) &&
          !organizationBlockedShareEmails.includes(email) &&
          !nextMembershipByEmail[email],
      );

      if (emailsToCheck.length) {
        const checks = await Promise.all(
          emailsToCheck.map(async (email) => {
            try {
              const status = (await projectsApi.checkUserProjectMembership(
                projectId,
                email,
              )) as ProjectMembership;
              return [email, status || { is_member: false }] as const;
            } catch {
              return [email, { is_member: false }] as const;
            }
          }),
        );

        if (!isCurrentSession(session, modelId)) return null;

        nextMembershipByEmail = {
          ...nextMembershipByEmail,
          ...Object.fromEntries(checks),
        };
        setMembershipByEmail(nextMembershipByEmail);
      }
    }

    if (typedEmails.length) {
      setEmails(shareEmails);
      setEmailInput("");
    }

    const blockedShareEmails = shareEmails.filter(
      (email) =>
        emailRegex.test(email) &&
        !organizationBlockedShareEmails.includes(email) &&
        nextMembershipByEmail[email]?.is_member === false,
    );
    const readyShareEmails = shareEmails.filter(
      (email) =>
        emailRegex.test(email) &&
        !organizationBlockedShareEmails.includes(email) &&
        nextMembershipByEmail[email]?.is_member !== false,
    );

    return {
      invalidShareEmails,
      organizationBlockedShareEmails,
      blockedShareEmails,
      readyShareEmails,
    };
  };

  const handleShare = async () => {
    const session = sessionRef.current;
    const modelId = model?.id;
    if (!modelId || submittingRef.current) return;

    submittingRef.current = true;
    setSubmitting(true);

    const payload = await getSharePayload(session, modelId);
    if (!payload || !isCurrentSession(session, modelId)) {
      if (isCurrentSession(session, modelId)) {
        submittingRef.current = false;
        setSubmitting(false);
      }
      return;
    }
    const {
      invalidShareEmails,
      organizationBlockedShareEmails,
      blockedShareEmails,
      readyShareEmails,
    } = payload;

    if (
      !readyShareEmails.length ||
      invalidShareEmails.length ||
      organizationBlockedShareEmails.length ||
      blockedShareEmails.length
    ) {
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    try {
      await Promise.all(
        readyShareEmails.map((email) =>
          llmApi.shareLLMConfig(modelId, {
            user: email,
            role: accessLevel,
          } as any),
        ),
      );

      if (!isCurrentSession(session, modelId)) return;

      toast.success(t("aiCatalog.shareDialog.modelSharedSuccess"));
      setEmails([]);
      setEmailInput("");
      setResolvedProjectEmails((previous) => {
        const next = { ...previous };
        readyShareEmails.forEach((email) => {
          delete next[email];
        });
        return next;
      });
      await loadSharedUsers(session, modelId);
      if (!isCurrentSession(session, modelId)) return;
      await onShared();
    } catch (error) {
      if (!isCurrentSession(session, modelId)) return;
      console.error("Error sharing model:", error);
      toast.error(
        (error as Error).message ||
          t("aiCatalog.shareDialog.failedToShareModel"),
      );
    } finally {
      if (isCurrentSession(session, modelId)) {
        submittingRef.current = false;
        setSubmitting(false);
      }
    }
  };

  const handleRemoveUserShare = async (email: string, user_id: string) => {
    const session = sessionRef.current;
    const modelId = model?.id;
    if (!modelId || !email || updatingShareRef.current.has(email)) return;
    const removedEmail = normalizeEmail(email);
    updatingShareRef.current.add(email);
    setUpdatingShare((previous) => ({ ...previous, [email]: true }));
    try {
      await llmApi.unshareByUserId(modelId, user_id);
      if (!isCurrentSession(session, modelId)) return;
      setSharedUsers((previous) =>
        previous.filter(
          (user) => normalizeEmail(getUserEmail(user)) !== removedEmail,
        ),
      );
      toast.success(t("aiCatalog.userAccessRemovedSuccess"));
      await loadSharedUsers(session, modelId);
      if (!isCurrentSession(session, modelId)) return;
      setSharedUsers((previous) =>
        previous.filter(
          (user) => normalizeEmail(getUserEmail(user)) !== removedEmail,
        ),
      );
    } catch (error) {
      if (!isCurrentSession(session, modelId)) return;
      console.error("Error removing user access:", error);
      toast.error(t("aiCatalog.failedToRemoveUserAccess"));
    } finally {
      updatingShareRef.current.delete(email);
      if (isCurrentSession(session, modelId)) {
        setUpdatingShare((previous) => ({ ...previous, [email]: false }));
      }
    }
  };

  const handleExistingAccessChange = async (
    user: ShareUser,
    nextAccessLevel: ExistingAccessLevel,
  ) => {
    const email = getUserEmail(user);
    const user_id = getUserId(user);
    const session = sessionRef.current;
    const modelId = model?.id;
    if (!email || !user_id || !modelId) return;

    if (nextAccessLevel === "remove") {
      handleRemoveUserShare(email, user_id);
      return;
    }

    if (updatingShareRef.current.has(email)) return;
    updatingShareRef.current.add(email);
    setUpdatingShare((previous) => ({ ...previous, [email]: true }));
    try {
      await llmApi.shareLLMConfig(modelId, {
        user: email,
        role: nextAccessLevel,
      } as any);
      if (!isCurrentSession(session, modelId)) return;
      setSharedUsers((previous) =>
        previous.map((item) =>
          getUserEmail(item) === email
            ? { ...item, access_level: nextAccessLevel }
            : item,
        ),
      );
      toast.success(t("aiCatalog.shareDialog.accessUpdated"));
      await onShared();
    } catch (error) {
      if (!isCurrentSession(session, modelId)) return;
      console.error("Error updating model share:", error);
      toast.error(
        (error as Error).message ||
          t("aiCatalog.shareDialog.failedToUpdateAccess"),
      );
    } finally {
      updatingShareRef.current.delete(email);
      if (isCurrentSession(session, modelId)) {
        setUpdatingShare((previous) => ({ ...previous, [email]: false }));
      }
    }
  };

  useEffect(() => {
    if (open) {
      const session = sessionRef.current;
      loadSharedUsers(session, model?.id);
    }
  }, [loadSharedUsers, model?.id, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!projectId) {
      return;
    }

    let isCurrent = true;
    const session = sessionRef.current;
    const modelId = model?.id;

    const loadLinkedProject = async () => {
      setProjectResolutionStatus("loading");
      try {
        const project = (await projectsApi.getProject(
          projectId,
          false,
        )) as Project;
        if (isCurrent && isCurrentSession(session, modelId)) {
          if (project?.id !== projectId) {
            setLinkedProject(null);
            setProjectResolutionStatus("error");
            return;
          }
          setLinkedProject(project);
          setProjectResolutionStatus("resolved");
        }
      } catch (error) {
        console.error("Error loading linked project details:", error);
        if (isCurrent && isCurrentSession(session, modelId)) {
          setLinkedProject(null);
          setProjectResolutionStatus("error");
        }
      }
    };

    loadLinkedProject();

    return () => {
      isCurrent = false;
    };
  }, [isCurrentSession, model?.id, open, projectId, projectLoadAttempt]);

  useEffect(() => {
    if (!open || !organizationId) {
      return;
    }

    let isCurrent = true;
    const session = sessionRef.current;
    const modelId = model?.id;

    const loadOrganizationMembers = async () => {
      setLoadingOrganizationMembers(true);
      try {
        const response = await organizationApi.getOrganizationMembers(
          organizationId,
        );
        if (isCurrent && isCurrentSession(session, modelId)) {
          setOrganizationMembers(
            Array.isArray(response.data) ? response.data : [],
          );
        }
      } catch (error) {
        console.error("Error loading organization members:", error);
        if (isCurrent && isCurrentSession(session, modelId)) {
          setOrganizationMembers([]);
        }
      } finally {
        if (isCurrent && isCurrentSession(session, modelId)) {
          setLoadingOrganizationMembers(false);
        }
      }
    };

    loadOrganizationMembers();

    return () => {
      isCurrent = false;
    };
  }, [isCurrentSession, model?.id, open, organizationId]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent
        data-testid="ai-catalog-share-dialog"
        aria-busy={hasPendingMutations}
        showCloseButton={false}
        onEscapeKeyDown={(event) => {
          if (hasPendingMutations) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (hasPendingMutations) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (hasPendingMutations) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          const trigger = returnFocusRef?.current;
          if (trigger?.isConnected && !trigger.disabled) {
            event.preventDefault();
            trigger.focus();
            return;
          }
          const fallback = fallbackFocusRef?.current;
          if (fallback?.isConnected) {
            event.preventDefault();
            fallback.focus();
          }
        }}
        className="grid h-[min(550px,calc(100dvh-32px))] w-[640px] max-w-[calc(100vw-32px)] grid-rows-[56px_minmax(0,1fr)] gap-0 overflow-hidden rounded-[10px] border bg-background p-0 shadow-xl sm:max-w-[640px]"
      >
        <DialogHeader className="flex h-14 flex-row items-center border-b border-border px-4 pe-12 text-start">
          <DialogTitle className="truncate text-xl leading-6 font-semibold">
            {t("aiCatalog.shareDialog.title", { name: modelName })}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("aiCatalog.shareDialog.description", { name: modelName })}
          </DialogDescription>
          <DialogClose asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              disabled={hasPendingMutations}
              data-testid="ai-catalog-share-close-button"
              aria-label={t("common.close")}
              className="absolute top-3.5 [inset-inline-end:1rem]"
            >
              <X aria-hidden="true" className="size-4" />
            </Button>
          </DialogClose>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto px-4 py-2">
          <label htmlFor="ai-catalog-share-email-input" className="mb-2 block text-xs font-medium text-muted-foreground">
            {t("aiCatalog.shareDialog.emails")}
          </label>

        <ShareEmailInput
          t={t}
          emails={emails}
          emailInput={emailInput}
          accessLevel={accessLevel}
          accessLevels={accessLevels}
          blockedEmails={blockedEmails}
          submitting={submitting}
          canShare={canShare}
          canSubmitModel={Boolean(model?.id)}
          onEmailInputChange={setEmailInput}
          onInputBlur={() => addEmails(emailInput)}
          onInputKeyDown={handleInputKeyDown}
          onRemoveEmail={removeEmail}
          onAccessLevelChange={setAccessLevel}
          onShare={handleShare}
          resolvedProjectEmails={resolvedProjectEmails}
        />

          {projectResolutionStatus === "error" ? (
            <div
              role="alert"
              className="mt-3 flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm"
            >
              <span>{t("aiCatalog.shareDialog.failedToLoadProject")}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setProjectResolutionStatus("loading");
                  setProjectLoadAttempt((attempt) => attempt + 1);
                }}
              >
                {t("common.retry")}
              </Button>
            </div>
          ) : null}

          {(projectResolutionEmails.length > 0 || invalidEmails.length > 0) && (
          <p role="alert" className="mt-2 text-xs text-muted-foreground">
            {projectResolutionEmails.length > 0
              ? t("aiCatalog.shareDialog.notProjectMembers", {
                  project: projectName,
                })
              : t("aiCatalog.shareDialog.fixInvalidEmails")}
          </p>
        )}

        {organizationBlockedEmails.length > 0 ? (
          <div role="alert" className="mt-3 rounded-md border border-destructive bg-destructive/10 p-3">
            <p className="text-xs font-medium text-destructive">
              {t("aiCatalog.shareDialog.notOrganizationMembers")}
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {organizationBlockedEmails.map((email) => (
                <span
                  key={email}
                  className="rounded bg-background px-2 py-1 text-xs text-foreground"
                  dir="ltr"
                >
                  {email}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        <ProjectResolutionList
          t={t}
          emails={projectResolutionEmails}
          addingToProject={addingToProject}
          resolvedProjectEmails={resolvedProjectEmails}
          disabled={!projectContextReady || loadingOrganizationMembers}
          onAddToProject={handleAddToProject}
          onRemoveEmail={removeEmail}
        />

        <p className="mt-6 mb-1 text-sm font-medium text-muted-foreground">
          {t("aiCatalog.shareDialog.sharedWith")}
        </p>

        <SharedUsersList
          t={t}
          users={sharedUsers}
          loading={loadingShares}
          error={sharesLoadError}
          onRetry={() => loadSharedUsers(sessionRef.current, model?.id)}
          currentUserId={cache?.id}
          accessLevels={accessLevels}
          updatingShare={updatingShare}
          onAccessChange={handleExistingAccessChange}
        />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AICatalogShareComp;
